import apiClient from './apiClient';
import { CreateLitterDTO, Litter, PedigreeResponse } from '@app-types/index';

/**
 * Serviciu pentru gestionarea fatarilor si pedigree-ului
 */

/**
 * Creeaza o fatare noua
 */
export const createLitter = async (data: CreateLitterDTO): Promise<{
  litterId: string;
  createdPups: string[];
  linkedPups: string[];
  errors?: string[];
}> => {
  const response = await apiClient.post('/litters', data);
  return response.data.data;
};

/**
 * Obtine detalii despre o fatare
 */
export const getLitter = async (litterId: string): Promise<Litter> => {
  const response = await apiClient.get(`/litters/${litterId}`);
  return response.data.data;
};

/**
 * Actualizeaza o fatare
 */
export const updateLitter = async (
  litterId: string,
  data: Partial<CreateLitterDTO>
): Promise<Litter> => {
  const response = await apiClient.patch(`/litters/${litterId}`, data);
  return response.data.data;
};

/**
 * Sterge o fatare
 */
export const deleteLitter = async (litterId: string): Promise<void> => {
  await apiClient.delete(`/litters/${litterId}`);
};

/**
 * Obtine fatarile unui animal
 */
export const getAnimalLitters = async (
  animalId: string,
  role: 'mother' | 'father' | 'both' = 'both'
): Promise<Litter[]> => {
  const response = await apiClient.get(`/litters/animal/${animalId}`, {
    params: { role },
  });
  return response.data.data;
};

/**
 * Obtine pedigree-ul unui animal
 */
export const getAnimalPedigree = async (
  animalId: string,
  upGenerations: number = 3,
  downGenerations: number = 1
): Promise<PedigreeResponse> => {
  const response = await apiClient.get(`/patients/${animalId}/pedigree`, {
    params: {
      up: upGenerations,
      down: downGenerations,
    },
  });
  return response.data.data;
};

/**
 * Obtine un animal dupa ID
 */
export const getAnimalById = async (animalId: string): Promise<any> => {
  const response = await apiClient.get(`/patients/${animalId}`);
  return response.data.data;
};

/**
 * Cauta animale dupa microchip sau nume (pentru validare live)
 */
export const lookupAnimals = async (query: string): Promise<any[]> => {
  try {
    // Normalizeaza query-ul: elimina paranteze si caractere non-alfanumerice excesive
    const q = (query || '')
      .replace(/\(.*?\)/g, '') // elimina textul din paranteze
      .replace(/[^\p{L}\p{N}\s.-]/gu, '') // pastreaza litere, cifre, spatii, punct, minus
      .trim();

    if (!q) return [];

  const response = await apiClient.get('/animals/lookup', {
      params: { q },
    });
    return response.data.data;
  } catch (err) {
    console.error('lookupAnimals failed:', err);
    return [];
  }
};

/**
 * Verifica daca un microchip este unic
 */
export const checkMicrochipUnique = async (
  microchip: string,
  excludeAnimalId?: string
): Promise<{ unique: boolean; animalId?: string; animal?: any }> => {
  try {
    const animals = await lookupAnimals(microchip);
    const existing = animals.find(
      (a) => a.microchipId === microchip && a.id !== excludeAnimalId
    );
    
    if (existing) {
      return {
        unique: false,
        animalId: existing.id,
        animal: existing,
      };
    }
    
    return { unique: true };
  } catch (error) {
    console.error('Error checking microchip:', error);
    return { unique: true }; // Presupunem ca e unic in caz de eroare
  }
};

const littersService = {
  createLitter,
  getLitter,
  updateLitter,
  deleteLitter,
  getAnimalLitters,
  getAnimalPedigree,
  getAnimalById,
  lookupAnimals,
  checkMicrochipUnique,
};

export default littersService;

